import { useMemo, useState } from "react";
import { Dialog, DialogContent, Grid, CircularProgress } from "@mui/material";
import { styled } from "@mui/system";
import Resume from "./Resume";

import Preview from "./Preview";
import MyBarChart from "../../../../utils/Chart/BarChart";
import { useQuery } from "react-query";
import { parseISO } from "date-fns";
import {
  getPerformanceFacebook,
  getStatusFacebook,
} from "../../../../services/metricsFacebook";
import {
  getPerformanceInstagram,
  getStatusInstagram,
} from "../../../../services/metricsInstagram";
import {
  getInsightsSends,
  getInsightsAds,
} from "../../../../services/metricsCampaigns";
import { ResponseErrorSocialNetworks } from "../../../../types/posts";
import { CampaignFromInsight, Campaigns } from "../../../../types/campaigns";
import { LoaderContainer } from "./styles";

const Div = styled("div")({});

interface CardDetailsProps {
  details: Campaigns;
  open: boolean;
  onClose: () => void;
  onPause: (id_campanha: number) => void;
  onPauseAds: (id: number) => void;
  onContinueAds: (id: number) => void;
  onContinue: (id_campanha: number) => void;
  onResend: (id: number, type: string) => void;
}

export default function CardDetails({
  open,
  details,
  onPause,
  onPauseAds,
  onContinueAds,
  onContinue,
  onClose,
  onResend,
}: CardDetailsProps) {
  const [fetchFacebook, setFetchFacebook] = useState(false);
  const [fetchInstagram, setFetchInstagram] = useState(false);
  const DATETIME_NOW = parseISO(new Date().toISOString());

  const isFetchFacebook = Boolean(
    details.tipo_campanha.includes("FACEBOOK") &&
      DATETIME_NOW > parseISO(details.data_envio)
  );

  const isFetchInstagram = Boolean(
    details.tipo_campanha.includes("INSTAGRAM") &&
      DATETIME_NOW > parseISO(details.data_envio)
  );

  const queryFbInsights = useQuery(
    "queryFbInsights",
    async () => getPerformanceFacebook(details?.id_campanha),
    {
      enabled: fetchFacebook,
      retry: false,
    }
  );

  const queryFbStatus = useQuery(
    ["queryFbStatus", details],
    async () => getStatusFacebook(details.id_campanha),
    {
      enabled: fetchFacebook,
      retry: false,
    }
  );

  const queryIgInsights = useQuery(
    "queryIgInsights",
    async () => getPerformanceInstagram(details?.id_campanha),
    {
      enabled: fetchInstagram,
      retry: false,
    }
  );

  const queryIgStatus = useQuery(
    ["queryIgStatus", details],
    async () => getStatusInstagram(details.id_campanha),
    {
      enabled: fetchInstagram,
      retry: false,
    }
  );

  const queryInsightsAds = useQuery(
    ["queryInsightsAds", details],
    // @ts-ignore: Unreachable code error
    async () => getInsightsAds(details?.id, details?.creative?.id),
    {
      retry: false,
      enabled: details.tipo_campanha === "ADS",
    }
  );

  const queryInsightsSends = useQuery(
    ["queryInsightsSends", details],
    async () => getInsightsSends(details.id_campanha,details.tipo_campanha),
    {
      retry: false,
      enabled: details.tipo_campanha !== "ADS",
      onSuccess: (data) => {
        if (isFetchFacebook && data.lista[0].data_processado) {
          setFetchFacebook(true);
        }
        if (isFetchInstagram && data.lista[0].data_processado) {
          setFetchInstagram(true);
        }
      },
    }
  );

  const isLoading = useMemo(() => {
    return (
      queryFbInsights.isLoading ||
      queryIgInsights.isLoading ||
      queryInsightsAds.isLoading ||
      queryInsightsSends.isLoading
    );
  }, [
    queryFbInsights.isLoading,
    queryIgInsights.isLoading,
    queryInsightsAds.isLoading,
    queryInsightsSends.isLoading,
  ]);

  const campaign = useMemo(() => {
    if (!isFetchFacebook || !isFetchInstagram) {
      return queryInsightsSends.data?.lista[0];
    }
    if (isFetchFacebook) {
      if (queryFbInsights.data?.campaign) {
        return queryFbInsights.data?.campaign[0] as CampaignFromInsight;
      } else if (queryFbInsights.data?.error === true) {
        return queryFbInsights.data as ResponseErrorSocialNetworks;
      }
    }
    if (isFetchInstagram) {
      if (queryIgInsights.data?.campaign) {
        return queryIgInsights.data?.campaign[0] as CampaignFromInsight;
      } else if (queryIgInsights.data?.error === true) {
        return queryIgInsights.data as ResponseErrorSocialNetworks;
      }
    }
    return undefined;
  }, [
    isFetchFacebook,
    isFetchInstagram,
    queryFbInsights.data,
    queryIgInsights.data,
    queryInsightsSends.data?.lista,
  ]);

  const reach = useMemo(() => {
    if (details) {
      if (details.tipo_campanha.includes("FACEBOOK")) {
        return queryFbStatus.data;
      } else if (details.tipo_campanha.includes("INSTAGRAM")) {
        return queryIgStatus.data;
      } else {
        return queryInsightsSends.data?.status;
      }
    }

    return [];
  }, [
    details,
    queryFbStatus.data,
    queryIgStatus.data,
    queryInsightsSends.data?.status,
  ]);

  let overview = queryInsightsAds?.data?.content?.overview || {};

  let overviewEdited = {
    Alcance: overview?.reach,
    Clicks: overview?.clicks,
    Impressões: overview?.impressions,
    Frequência: overview?.frequency,
  };

  // @ts-ignore: Unreachable code error
  overviewEdited = Object.keys(overviewEdited).map(function (key) {
    return {
      status_envio_externo: key,
      // @ts-ignore: Unreachable code error
      quantidade: overviewEdited[key],
    };
  });

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="lg" onClose={onClose}>
        <DialogContent dividers>
          <Grid
            container
            columnSpacing={4}
            sx={{
              fontFamily: "Montserrat",
            }}
          >
            {isLoading ? (
              <Grid item xs={12}>
                <LoaderContainer>
                  <CircularProgress color="primary" />
                </LoaderContainer>
              </Grid>
            ) : (
              <>
                <Grid
                  item
                  xs={5}
                  sx={{
                    display: "flex",
                    marginTop: "2.25rem",
                    marginBottom: "2.25rem",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Preview
                    campaign={
                      details.tipo_campanha !== "ADS"
                        ? (campaign as CampaignFromInsight)
                        : details
                    }
                    adsImgIframe={
                      queryInsightsAds.data?.content.effective_object_story_id
                    }
                    type={details.tipo_campanha}
                  />
                </Grid>
                <Grid
                  item
                  xs={7}
                  sx={{
                    marginTop: "1rem",
                  }}
                >
                  <Grid
                    container
                    sx={{ display: "block", justifyContent: "space-between" }}
                  >
                    <Resume
                      overview={overviewEdited}
                      reach={reach}
                      campaign={details}
                      insights={
                        details.tipo_campanha !== "ADS"
                          ? (campaign as CampaignFromInsight)
                          : undefined
                      }
                      onClose={onClose}
                      onPause={onPause}
                      onPauseAds={onPauseAds}
                      onContinueAds={onContinueAds}
                      onContinue={onContinue}
                      onResend={onResend}
                    />
                  </Grid>
                </Grid>
                {details.tipo_campanha === "ADS" && (
                  <Div style={{ marginLeft: "24px", width: "100%" }}>
                    <Div style={{ display: "flex", flexWrap: "wrap" }}>
                      {[
                        {
                          data: queryInsightsAds?.data?.content?.platforms,
                          tooltipFormatter: "Plataformas",
                          barDataKey: "reach",
                          xDataKey: "publisher_platform",
                        },
                        {
                          data: queryInsightsAds?.data?.content?.device,
                          tooltipFormatter: "Dispositivos",
                          barDataKey: "reach",
                          xDataKey: "device_platform",
                        },
                        {
                          data: queryInsightsAds?.data?.content?.gender,
                          tooltipFormatter: "Gênero",
                          barDataKey: "spend",
                          xDataKey: "gender",
                        },
                        {
                          data: queryInsightsAds?.data?.content?.age,
                          tooltipFormatter: "Idade",
                          barDataKey: "spend",
                          xDataKey: "age",
                        },
                      ].map((graph, index) => (
                        <>
                          {Boolean(graph.data?.length) && (
                            <Div style={{ width: "100%", margin: 16 }}>
                              <MyBarChart
                                key={index}
                                data={graph.data}
                                barLegend
                                // @ts-ignore: Unreachable code error
                                randomFill
                                barDataKey={graph.barDataKey}
                                xDataKey={graph.xDataKey}
                                tooltipFormatter={graph.tooltipFormatter}
                              />
                            </Div>
                          )}
                        </>
                      ))}
                    </Div>
                  </Div>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}
